/**
 * useFeedBlocks — блоки-ленты страницы (секции `trend_feed` админки) для любого раздела /bots.
 *
 * Логика переехала сюда из BotsTrends (12.09), когда владелец попросил такие же блоки на странице
 * изображений: сама механика от раздела не зависит — лента объявляет вид и позицию, состав берётся
 * либо вручную (карточка отмечает ленту в `feeds[]`), либо авто-правилом по пулу страницы.
 * Страница передаёт свои пулы: `rawPool` — ВСЕ карточки секций без лейн-фильтра (ручной состав
 * не ограничен лейном карточки), `lanePool` — видимые карточки своего лейна (авто-источники).
 * Глазик страницы сильнее отметки: карточка, скрытая на этой странице, в её блок не попадает —
 * кроме трендов, где лента и есть витрина и состав кураторский (см. usesPageCards).
 */
import { useMemo, useState } from 'react'

import {
	applyFeedCardOrder,
	normalizeTrendFeedView,
	type TrendFeedArrangement,
	type TrendFeedTile,
} from '../../../components/TrendFeedSection.tsx'
import { activeDiscount, trendDiscount, type TrendPipePricing } from '../../../utils/discount'
import type { BackendCard } from '../utils/popularCardApply.ts'
import type { ToolPage } from '../utils/cardSeo.ts'
import type { PopularSectionBase } from './usePopularSections'

/** Тренды рисуют блок своими плитками (TrendFeedSection); остальные разделы — карточкой
 *  самой страницы. От этого зависит и форма плиток, и правило видимости ниже. */
export const usesPageCards = (page: ToolPage): boolean => page !== 'trends'

/** Блок ленты: секция + резолвнутые участники и hero. Формат плитки (tile) и раскладка
 *  (arrangement) — независимые параметры (нормализация легаси-конфигов — normalizeTrendFeedView). */
export interface FeedBlock {
	section: PopularSectionBase
	tile: TrendFeedTile
	arrangement: TrendFeedArrangement
	members: BackendCard[]
	hero: BackendCard | null
	visibleCount: number
	/** Сколько РЯДОВ сетки видно до «Показать ещё» (0 — настройка не задана:
	 *  работает легаси-правило по visibleCount). Ряды, а не штуки: колонок на
	 *  телефоне и на столе разное число, и «два ряда» — единственная величина,
	 *  которая означает одно и то же на любой ширине. */
	visibleRows: number
	/** Размер карточек блока: 'lg' — крупные (настройка блока). Пусто — обычные.
	 *  Действует только там, где блок рисует плитки ленты (тренды/главная): на
	 *  разделах форму задаёт карточка самой страницы. */
	cardSize: string
	theme: string
	bgColor: string
	title: string
	subtitle?: string
	icon?: string
	/** Секция скрыта глазиком — видна только в стафф-превью (?preview=1). */
	hidden: boolean
}

/** Карточки, которые уже показаны блоками страницы, — чтобы не повторять их в общей сетке.
 *
 *  Разбивка на блоки затем и придумана, чтобы получилась понятная структура
 *  (владелец 14.09): карточка, уехавшая в блок, не должна вторым экземпляром
 *  лежать в огромном массиве ниже. Считаем по тем блокам, которые страница
 *  реально рисует: скрытые глазиком до фронта не доезжают вовсе, а в
 *  стафф-превью они видимы — и там повтор тоже не нужен.
 *
 *  Герой блока входит в набор: в раскладке `hero` он живёт отдельным полем, а не
 *  в составе, и без него шапка блока дублировалась бы сеткой.
 */
export function blockCardIds(blocks: FeedBlock[]): Set<string> {
	const ids = new Set<string>()
	for (const b of blocks) {
		if (b.hero) ids.add(String(b.hero.id))
		for (const m of b.members) ids.add(String(m.id))
	}
	return ids
}

/** Поля карточки, которыми оперируют авто-источники состава (все опциональны — бэк-энрич и легаси). */
type AutoCard = BackendCard & {
	feeds?: string[]
	used_count?: number
	used_7d?: number
	created_at?: string
	category_by_page?: Record<string, string>
	pipeline?: TrendPipePricing
	price_override?: number
	discount_price?: number
	discount_until?: string
}

const used7Of = (c: AutoCard) => Number(c.used_7d ?? 0) || 0
const usedAllOf = (c: AutoCard) => Number(c.used_count ?? 0) || 0
// Активная скидка — та же формула, что у плашки «−N%» на плитке.
const hasPromo = (c: AutoCard) =>
	c.pipeline?.enabled
		? !!trendDiscount(c.pipeline)
		: !!activeDiscount(c.price_override, c.discount_price, c.discount_until)

// Детерминированный PRNG для order_mode='shuffle': сид берётся один раз на маунт
// (useState-инициализатор — санкционированное место для Math.random), сам шафл в memo
// остаётся чистой функцией входов.
export const mulberry32 = (seed: number) => () => {
	seed |= 0
	seed = (seed + 0x6d2b79f5) | 0
	let t = Math.imul(seed ^ (seed >>> 15), 1 | seed)
	t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
	return ((t ^ (t >>> 14)) >>> 0) / 4294967296
}

export interface FeedBlocksInput {
	page: ToolPage
	sections: PopularSectionBase[] | null
	/** ВСЕ карточки секций страницы — ручной состав (feeds[] карточки). */
	rawPool: BackendCard[]
	/** Видимые карточки лейна — авто-источники (популярные, новинки, скидки, категория). */
	lanePool: BackendCard[]
}

/** Чистая сборка блоков: вход → блоки. rnd передаётся снаружи, чтобы функция
 *  оставалась детерминированной и проверяемой тестом. */
export function buildFeedBlocks({ page, sections, rawPool, lanePool }: FeedBlocksInput, rnd: () => number): FeedBlock[] {
	{
		const out: FeedBlock[] = []
		for (const sec of sections ?? []) {
			if (sec.section_type !== 'trend_feed') continue
			// Лента чужой страницы (target_pages в админке) сюда не приезжает: бэк отдаёт
			// секции запрошенной страницы, дополнительный фильтр не нужен.
			const cfg = sec.config as Record<string, unknown>
			const { tile, arrangement } = normalizeTrendFeedView(cfg.layout, cfg.tile)

			// ── Источник состава: manual (feeds[] карточек) или авто-правило по пулу страницы
			// (категория/популярные/новинки/скидки). Авто-режимы уважают видимость лейна всегда,
			// ручной — на страницах с карточками раздела (на трендах состав кураторский).
			const src = String(cfg.source || 'manual')
			const srcLimit = Math.max(1, Math.round(Number(cfg.source_limit) || 12))
			let members: AutoCard[]
			if (src === 'manual') {
				members = (rawPool as AutoCard[]).filter((c) => Array.isArray(c.feeds) && c.feeds.includes(sec.id))
				// Карточка, скрытая глазиком НА ЭТОЙ странице, не показывается и в её блоках
				// (владелец 12.09): «скрыл — значит не показывай» сильнее кураторской отметки.
				// Тренды — исключение: там лента и есть витрина, ручной состав видимость лейна
				// игнорирует намеренно.
				if (usesPageCards(page)) members = members.filter((c) => !c.hidden_pages?.includes(page))
			} else {
				let base = lanePool as AutoCard[]
				if (src === 'category') {
					const cat = String(cfg.source_category || '')
					base = cat ? base.filter((c) => c.category_by_page?.[page] === cat) : []
				} else if (src === 'discounted') {
					base = base.filter(hasPromo)
				}
				if (src === 'popular' || src === 'discounted') {
					base = [...base].sort((a, b) => used7Of(b) - used7Of(a) || usedAllOf(b) - usedAllOf(a))
				} else if (src === 'newest') {
					const ts = (c: AutoCard) => Date.parse(String(c.created_at || '')) || 0
					base = [...base].sort((a, b) => ts(b) - ts(a))
				}
				// category: порядок пула = админский порядок галереи
				members = base.slice(0, srcLimit)
			}

			// ── Порядок поверх состава ──
			const orderMode = String(cfg.order_mode || 'admin')
			if (orderMode === 'admin') {
				// Свой порядок ленты (DnD по плиткам блока в админке). Пусто — порядок пула.
				members = applyFeedCardOrder(members, cfg.card_order)
			} else if (orderMode === 'popular') {
				members = [...members].sort((a, b) => used7Of(b) - used7Of(a) || usedAllOf(b) - usedAllOf(a))
			} else if (orderMode === 'shuffle') {
				members = [...members]
				for (let i = members.length - 1; i > 0; i--) {
					const j = Math.floor(rnd() * (i + 1))
					;[members[i], members[j]] = [members[j], members[i]]
				}
			}

			// ── Hero: авто (топ за 7 дней) или ручной выбор (серверный резолв) ──
			const heroMode = String(cfg.hero_mode || 'manual')
			const heroPick =
				heroMode === 'auto'
					? ([...members].sort((a, b) => used7Of(b) - used7Of(a) || usedAllOf(b) - usedAllOf(a))[0] ?? null)
					: (((cfg.hero_card as BackendCard | null | undefined) || members[0]) ?? null)
			// Скрытая на странице карточка не становится и «главной» блока.
			const hero =
				heroPick && usesPageCards(page) && heroPick.hidden_pages?.includes(page) ? null : heroPick
			if (arrangement === 'hero' ? !hero : members.length === 0) continue
			out.push({
				section: sec,
				tile,
				arrangement,
				members,
				hero,
				hidden: (sec as { is_visible?: boolean }).is_visible === false,
				visibleCount: Math.max(1, Math.round(Number(cfg.visible_count) || 10)),
				visibleRows: Math.max(0, Math.round(Number(cfg.visible_rows) || 0)),
				cardSize: String(cfg.card_size || ''),
				theme: String(cfg.theme || ''),
				bgColor: String(cfg.bg_color || ''),
				title: String(cfg.title || ''),
				subtitle: cfg.subtitle ? String(cfg.subtitle) : undefined,
				icon: cfg.icon ? String(cfg.icon) : undefined,
			})
		}
		return out
	}
}

export function useFeedBlocks(input: FeedBlocksInput): FeedBlock[] {
	// Сид шафла: стабилен между ре-рендерами, новый — при новом заходе.
	const [shuffleSeed] = useState(() => Math.floor(Math.random() * 0x7fffffff))
	const { page, sections, rawPool, lanePool } = input
	return useMemo(
		() => buildFeedBlocks({ page, sections, rawPool, lanePool }, mulberry32(shuffleSeed)),
		[page, sections, rawPool, lanePool, shuffleSeed],
	)
}

/** Все карточки секций страницы БЕЗ лейн-фильтра — пул для ручного состава лент. */
export function rawCardsOf(sections: PopularSectionBase[] | null): BackendCard[] {
	const out: BackendCard[] = []
	for (const sec of sections ?? []) {
		if (sec.section_type !== 'popular_category') continue
		const cards = Array.isArray(sec.config.cards) ? (sec.config.cards as BackendCard[]) : []
		out.push(...cards)
	}
	return out
}
